/**
 * @fileoverview ClassificationActivity Component - Clean, Compact Design
 * @module ClassificationActivity
 * 
 * Used for Quality and Condition Misclassification flaws in Step 3.
 * Redesigned for better information density and cleaner form flow.
 */

import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Loader2, 
  FileText, 
  Download, 
  ExternalLink,
  Plus,
  X,
  ChevronDown,
  CheckCircle2,
  Lightbulb,
  Link2
} from 'lucide-react';

export interface ClassificationResource {
  id: string;
  title: string;
  description: string;
  url: string;
  countySpecific: boolean;
  flawType?: string;
}

export interface TextboxConfig {
  instructions: string;
  ratingLabel: string;
  ratingPlaceholder: string;
  findingsLabel: string;
  findingsPlaceholder: string;
  categories?: string[];
}

export interface MisclassificationItem {
  id: string;
  category: string;
  description: string;
}

export interface TextboxFlawData extends Record<string, string | MisclassificationItem[]> {
  countyRating: string;
  items: MisclassificationItem[];
}

export interface ClassificationActivityProps {
  flawType: string;
  config: TextboxConfig;
  resources: ClassificationResource[];
  initialData?: Partial<TextboxFlawData>;
  initialNotes?: string;
  onSave: (data: { 
    checklistData: TextboxFlawData; 
    followUpData: Record<string, Record<string, string | number>>;
    notes: string;
  }) => Promise<void>;
  isSaving?: boolean;
  /** Number of items pre-populated from Step 2 maintenance data */
  prePopulatedItemCount?: number;
}

const QUALITY_CATEGORIES = [
  { value: 'Countertops', hint: 'Granite vs laminate' },
  { value: 'Flooring', hint: 'Hardwood vs vinyl' },
  { value: 'Cabinets', hint: 'Custom vs builder-grade' },
  { value: 'Fixtures', hint: 'Faucets, lighting' },
  { value: 'Windows', hint: 'Single vs double-pane' },
  { value: 'Appliances', hint: 'Age, brand tier' },
  { value: 'Exterior', hint: 'Siding, brick, paint' },
  { value: 'Interior', hint: 'Trim, doors, molding' },
  { value: 'Roof', hint: 'Shingle type, age' },
  { value: 'HVAC', hint: 'Age, efficiency' },
  { value: 'Other', hint: '' }
];

const CONDITION_CATEGORIES = [
  { value: 'Roof', hint: 'Leaks, missing shingles' },
  { value: 'HVAC', hint: 'Heating/cooling issues' },
  { value: 'Foundation', hint: 'Cracks, settling' },
  { value: 'Plumbing', hint: 'Leaks, pressure' },
  { value: 'Electrical', hint: 'Wiring, panel' },
  { value: 'Exterior', hint: 'Siding, paint, rot' },
  { value: 'Walls/Ceilings', hint: 'Cracks, stains' },
  { value: 'Windows/Doors', hint: 'Drafts, seals' },
  { value: 'Kitchen', hint: 'Cabinets, appliances' },
  { value: 'Bathrooms', hint: 'Fixtures, tile' },
  { value: 'Flooring', hint: 'Wear, damage' },
  { value: 'Other', hint: '' }
];

const generateItemId = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const ClassificationActivity: React.FC<ClassificationActivityProps> = ({
  flawType,
  config,
  resources,
  initialData,
  initialNotes = '',
  onSave,
  isSaving = false,
  prePopulatedItemCount = 0
}) => {
  const categories = flawType === 'QUALITY_MISCLASSIFICATION' ? QUALITY_CATEGORIES : CONDITION_CATEGORIES;
  const isQuality = flawType === 'QUALITY_MISCLASSIFICATION';

  const getInitialItems = (): MisclassificationItem[] => {
    if (!initialData) return [];
    if (Array.isArray(initialData.items)) return initialData.items;
    if (typeof initialData.misclassifications === 'string' && initialData.misclassifications.trim()) {
      const lines = initialData.misclassifications.split(/[\n•\-\*]/).map(line => line.trim()).filter(line => line.length > 0);
      return lines.map(line => ({ id: generateItemId(), category: 'Other', description: line }));
    }
    return [];
  };

  const [countyRating, setCountyRating] = useState((initialData?.countyRating as string) || '');
  const [items, setItems] = useState<MisclassificationItem[]>(getInitialItems);
  const [notes, setNotes] = useState(initialNotes);
  const [hasChanges, setHasChanges] = useState(false);
  const [downloadingResource, setDownloadingResource] = useState<string | null>(null);
  
  const [newCategory, setNewCategory] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    setCountyRating((initialData?.countyRating as string) || '');
    setItems(getInitialItems());
    setNotes(initialNotes);
    setHasChanges(false);
    setNewCategory('');
    setNewDescription('');
    setShowAddForm(false);
  }, [flawType, initialData, initialNotes]);

  const handleAddItem = () => {
    if (!newCategory || !newDescription.trim()) return;
    setItems([...items, { id: generateItemId(), category: newCategory, description: newDescription.trim() }]);
    setNewCategory('');
    setNewDescription('');
    setShowAddForm(false);
    setHasChanges(true);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave({ 
      checklistData: { 
        countyRating, 
        items,
        misclassifications: items.map(i => `${i.category}: ${i.description}`).join('\n')
      },
      followUpData: {},
      notes 
    });
    setHasChanges(false);
  };

  const handleResourceDownload = (resource: ClassificationResource) => {
    setDownloadingResource(resource.id);
    window.open(resource.url, '_blank');
    setTimeout(() => setDownloadingResource(null), 1000);
  };

  const hasContent = countyRating.trim().length > 0 || items.length > 0;
  const currentCategoryHint = categories.find(c => c.value === newCategory)?.hint || '';
  const genericResources = resources.filter(r => !r.countySpecific);
  const countyResources = resources.filter(r => r.countySpecific);

  // Check if an item is pre-populated from Step 2 maintenance data
  const isPrePopulatedItem = (itemId: string) => itemId.startsWith('maintenance-');

  return (
    <div className="space-y-4">
      {/* Pre-populated Items Banner - Show when items came from Step 2 */}
      {prePopulatedItemCount > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-teal-50 rounded-lg border border-teal-200">
          <Link2 className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-teal-800">
            <span className="font-medium">Step 2 data imported:</span>{' '}
            We've added {prePopulatedItemCount} issue{prePopulatedItemCount !== 1 ? 's' : ''} from your maintenance documentation. Review and add any additional condition concerns below.
          </div>
        </div>
      )}

      {/* Quick Tip */}
      <div className="flex items-start gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
        <Lightbulb className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700">{config.instructions}</p>
      </div>

      {/* Reference Guides - Expanded by default */}
      {(genericResources.length > 0 || countyResources.length > 0) && (
        <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/80 to-slate-50/50 overflow-hidden">
          <details className="group" open>
            <summary className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-blue-50/50 transition-colors">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-800">Reference Guides</span>
                  <span className="ml-2 text-xs text-blue-600 font-medium">({genericResources.length + countyResources.length} available)</span>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
            </summary>
            
            <div className="px-4 pb-4 space-y-3">
              {/* How to use description */}
              <p className="text-xs text-gray-500 leading-relaxed">
                Review these guides to understand how {isQuality ? 'quality' : 'condition'} ratings work. 
                Compare the descriptions against your property to identify misclassifications.
              </p>
              
              {/* County-specific guides first (if any) */}
              {countyResources.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Your County</span>
                    <div className="flex-1 h-px bg-teal-200"></div>
                  </div>
                  {countyResources.map((resource) => (
                    <button
                      key={resource.id}
                      onClick={() => handleResourceDownload(resource)}
                      disabled={downloadingResource === resource.id}
                      className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg border-2 border-teal-200 bg-teal-50 hover:bg-teal-100 hover:border-teal-300 text-left transition-all group/btn"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-teal-200/70 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-teal-700" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800 truncate">{resource.title}</span>
                            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-teal-200 text-teal-800 rounded">Recommended</span>
                          </div>
                          <p className="text-[11px] text-gray-500 truncate">{resource.description}</p>
                        </div>
                      </div>
                      {downloadingResource === resource.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-teal-500 flex-shrink-0" />
                      ) : (
                        <ExternalLink className="w-4 h-4 text-teal-500 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Generic guides */}
              {genericResources.length > 0 && (
                <div className="space-y-2">
                  {countyResources.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">General Reference</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                  )}
                  {genericResources.map((resource) => (
                    <button
                      key={resource.id}
                      onClick={() => handleResourceDownload(resource)}
                      disabled={downloadingResource === resource.id}
                      className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-left transition-all group/btn"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-700 truncate block">{resource.title}</span>
                          <p className="text-[11px] text-gray-400 truncate">{resource.description}</p>
                        </div>
                      </div>
                      {downloadingResource === resource.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
                      ) : (
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover/btn:text-gray-600 transition-colors flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </details>
        </div>
      )}

      {/* County Rating Input */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {config.ratingLabel}
        </label>
        <input
          type="text"
          value={countyRating}
          onChange={(e) => { setCountyRating(e.target.value); setHasChanges(true); }}
          placeholder={config.ratingPlaceholder}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
        />
        <p className="text-[10px] text-gray-400 mt-1">From your property record card or county evidence</p>
      </div>

      {/* Findings List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">{config.findingsLabel}</label>
          {items.length > 0 && (
            <span className="text-xs text-teal-600 font-medium">{items.length} found</span>
          )}
        </div>
        
        {/* Items List - Compact Cards */}
        {items.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {items.map((item, index) => {
              const isFromMaintenance = isPrePopulatedItem(item.id);
              return (
                <div 
                  key={item.id}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg group ${
                    isFromMaintenance 
                      ? 'bg-teal-50 border border-dashed border-teal-200' 
                      : 'bg-slate-50 border border-slate-200'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    isFromMaintenance 
                      ? 'bg-teal-200 text-teal-700' 
                      : 'bg-teal-100 text-teal-700'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                        isFromMaintenance 
                          ? 'bg-teal-200/70 text-teal-800' 
                          : 'bg-slate-200/70 text-slate-700'
                      }`}>
                        {item.category}
                      </span>
                      {isFromMaintenance && (
                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-teal-100 text-teal-600 text-[9px] font-medium rounded" title="Imported from Step 2 maintenance">
                          <Link2 className="w-2.5 h-2.5" />
                          Step 2
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-tight">{item.description}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title={isFromMaintenance ? "Remove imported item" : "Remove item"}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Finding */}
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50/50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {items.length === 0 ? 'Add finding' : 'Add another'}
          </button>
        ) : (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
            <div className="relative">
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none"
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.value}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            
            {newCategory && currentCategoryHint && (
              <p className="text-[10px] text-teal-600 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" />
                {currentCategoryHint}
              </p>
            )}

            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder={isQuality ? "What's wrong? (e.g., 'County says granite, I have laminate')" : "What's the issue? (e.g., 'Roof is 18 years old with visible wear')"}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
            />

            <div className="flex gap-2">
              <button
                onClick={() => { setShowAddForm(false); setNewCategory(''); setNewDescription(''); }}
                className="flex-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={!newCategory || !newDescription.trim()}
                className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  newCategory && newDescription.trim()
                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
          </div>
        )}

        {/* Empty State Tip */}
        {items.length === 0 && !showAddForm && (
          <p className="text-xs text-gray-500 mt-2 text-center">
            {isQuality 
              ? "Compare your actual finishes to what the county says"
              : "Think about wear, age, and maintenance issues"}
          </p>
        )}
      </div>

      {/* Notes - Collapsed */}
      <details className="group">
        <summary className="flex items-center gap-1 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">
          <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
          Additional notes
        </summary>
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setHasChanges(true); }}
          placeholder="Any other context..."
          rows={2}
          className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
        />
      </details>

      {/* Save Bar */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {items.length > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {items.length} finding{items.length !== 1 ? 's' : ''}
            </span>
          )}
          {hasChanges && items.length === 0 && (
            <span className="text-slate-500">• Unsaved</span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving || !hasContent}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            hasChanges && !isSaving && hasContent
              ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5" />
              Save
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ClassificationActivity;
